// src/components/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { 
  submitLoginRequest,
  submitForgotPasswordRequest,
  verifyCodeRequest,
  resetPasswordRequest,
  clearForgotPasswordError,
} from "../reducers/login.reducer";
import { 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Box,
  Tooltip,
} from "@mui/material";
import { 
  selectLoggedIn, 
  selectLoginError,
  selectForgotPasswordLoading,
  selectForgotPasswordError,
  selectForgotPasswordSuccess,
} from "../selectors/login.selector";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  // Login states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailRequiredError, setEmailRequiredError] = useState(null);
  const [passwordRequiredError, setPasswordRequiredError] = useState(null);

  // Forgot password states
  const [mode, setMode] = useState('login'); // 'login', 'verify', 'reset'
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [lastAction, setLastAction] = useState(null); // Track what action was last performed

  const error = useSelector(selectLoginError);
  const forgotPasswordError = useSelector(selectForgotPasswordError);
  const forgotPasswordLoading = useSelector(selectForgotPasswordLoading);
  const forgotPasswordSuccess = useSelector(selectForgotPasswordSuccess);
  const loggedIn = useSelector(selectLoggedIn);

  console.log("params", params);

  useEffect(() => {
    if (loggedIn) {
      navigate("/suppliers");
    }
  }, [loggedIn, navigate]);

  // Handle successful API responses to move to next stage
  useEffect(() => {
    if (
      forgotPasswordSuccess &&
      !forgotPasswordLoading &&
      !forgotPasswordError
    ) {
      if (lastAction === 'forgotPassword') {
        setMode('verify');
        setCode(['', '', '', '', '', '']);
        setLastAction(null);
        dispatch(clearForgotPasswordError());
      } else if (lastAction === 'verifyCode') {
        setMode('reset');
        setLastAction(null);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        dispatch(clearForgotPasswordError());
      } else if (lastAction === 'resetPassword') {
        setMode('login');
        setLastAction(null);
        setCode(['', '', '', '', '', '']);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        dispatch(clearForgotPasswordError());
        alert(
          'Password reset successful! Please login with your new password.'
        );
      }
    }
  }, [
    forgotPasswordSuccess,
    forgotPasswordLoading,
    lastAction,
    forgotPasswordError,
    dispatch,
  ]);

  // Clear lastAction and relevant data when there's an error
  useEffect(() => {
    if (forgotPasswordError) {
      if (lastAction === 'verifyCode') {
        // Reset the code inputs when verification fails
        setCode(['', '', '', '', '', '']);
      }
      setLastAction(null);
    }
  }, [forgotPasswordError, lastAction]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setEmailRequiredError(
      email.trim().length === 0 ? "Please enter the email." : null
    );
    setPasswordRequiredError(
      password.trim().length === 0 ? "Please enter the password." : null
    );

    if (email.trim().length > 0 && password.trim().length > 0) {
      dispatch(submitLoginRequest({ email, password }));
    }
  };

  const handleForgotPassword = () => {
    if (!email.trim()) {
      setShowEmailTooltip(true);
      setTimeout(() => setShowEmailTooltip(false), 3000);
      return;
    }
    // Clear the password field when initiating forgot password flow
    setPassword('');
    setPasswordRequiredError(null);
    setLastAction('forgotPassword');
    dispatch(submitForgotPasswordRequest({ email }));
  };

  const handleCodeChange = (index, value) => {
    // Handle pasting of multiple characters
    if (value.length > 1) {
      // Remove any non-digit characters and limit to 6 characters
      const cleanValue = value.replace(/\D/g, '').slice(0, 6);

      if (cleanValue.length > 0) {
        const newCode = ['', '', '', '', '', ''];

        // Fill the boxes starting from index 0 (always start from beginning when pasting)
        for (let i = 0; i < cleanValue.length && i < 6; i++) {
          newCode[i] = cleanValue[i];
        }

        setCode(newCode);

        // Focus on the next empty box or the last filled box
        const nextIndex = Math.min(cleanValue.length, 5);
        const nextInput = document.getElementById(`code-${nextIndex}`);
        if (nextInput) {
          setTimeout(() => nextInput.focus(), 0);
        }
      }
      return;
    }

    // Handle single character input (existing logic)
    if (value.length <= 1) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input when typing
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace for sequential clearing
    if (e.key === 'Backspace') {
      const newCode = [...code];

      // If current box is empty and we're not at the first box
      if (!newCode[index] && index > 0) {
        // Clear the previous box and focus on it
        newCode[index - 1] = '';
        setCode(newCode);
        const prevInput = document.getElementById(`code-${index - 1}`);
        if (prevInput) prevInput.focus();
      } else {
        // Clear the current box
        newCode[index] = '';
        setCode(newCode);
      }
    }
  };

  const handleCodeSubmit = () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      return;
    }
    setLastAction('verifyCode');
    dispatch(verifyCodeRequest({ email, code: fullCode }));
    // Don't change mode here - wait for success response
  };

  const handlePasswordReset = e => {
    e.preventDefault();
    
    // Add minimum length validation
    if (newPassword.length < 5) {
      setPasswordError('Password must be at least 5 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setPasswordError('');
    setLastAction('resetPassword');
    dispatch(resetPasswordRequest({ email, code: code.join(''), newPassword }));
    // Don't change mode here - wait for success response
  };

  const handleBackToLogin = () => {
    setMode('login');
    // Clear all forgot password data for clean slate
    setCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setLastAction(null);
    dispatch(clearForgotPasswordError());
  };

  const renderLoginForm = () => (
    <>
      <Typography
        style={{
          fontWeight: 600,
          fontSize: 24,
          lineHeight: "32px",
          color: "#101828",
        }}
      >
        Welcome back to
      </Typography>
      <Typography
        style={{
          fontWeight: 600,
          fontSize: 24,
          lineHeight: "32px",
          color: "#101828",
        }}
      >
        Elm AI - Supplier Portal
      </Typography>
      <Typography
        style={{
          fontWeight: 400,
          fontSize: 16,
          lineHeight: "24px",
        }}
      >
        Please enter your details to login.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Tooltip
          title="Please enter your email first"
          open={showEmailTooltip}
          placement="top"
          arrow
        >
          <TextField
            label="Enter your email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailRequiredError}
            helperText={emailRequiredError}
            fullWidth
            variant="outlined"
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "8px", // Set the border-radius here
                },
              },
              "& .MuiOutlinedInput-input": {
                height: "40px", // Control the height of the input
                padding: "0 14px", // Adjust padding as needed
              },
              "& .MuiInputLabel-outlined": {
                lineHeight: "40px", // Align the label height with the input height
                transform: "translate(14px, 0px) scale(1)", // Adjust label position
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -14px) scale(0.75)", // Adjust label position when focused
              },
            }}
            InputProps={{
              style: {
                fontFamily: "Inter, sans-serif", // Set the font family for the input text
                alignItems: "center",
                fontSize: "14px",

                // Center align the text vertically
              },
            }}
            InputLabelProps={{
              style: {
                fontSize: "12px",
                margin: 0,
                padding: 0,
              },
            }}
          />
        </Tooltip>
        <TextField
          label="Enter your password"
          type="password"
          value={password}
          error={!!passwordRequiredError}
          helperText={passwordRequiredError}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          variant="outlined"
          sx={{
            mt: 3,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderRadius: "8px", // Set the border-radius here
              },
            },
            "& .MuiOutlinedInput-input": {
              height: "40px", // Control the height of the input
              padding: "0 14px", // Adjust padding as needed
            },
            "& .MuiInputLabel-outlined": {
              lineHeight: "40px", // Align the label height with the input height
              transform: "translate(14px, 0px) scale(1)", // Adjust label position
            },
            "& .MuiInputLabel-shrink": {
              transform: "translate(14px, -14px) scale(0.75)", // Adjust label position when focused
            },
          }}
          InputProps={{
            style: {
              fontFamily: "Inter, sans-serif", // Set the font family for the input text
              alignItems: "center",
              fontSize: "14px",

              // Center align the text vertically
            },
          }}
          InputLabelProps={{
            style: {
              fontSize: "12px",
              margin: 0,
              padding: 0,
            },
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            mt: 6,
            mb: 2,
            backgroundColor: "#020A52",
            borderRadius: "8px",
            textTransform: "none",
            gap: "6px",
            padding: "10px 16px",

            fontSize: 16,
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          Sign In
        </Button>
        <Button
          fullWidth
          variant="text"
          disabled={forgotPasswordLoading}
          sx={{
            mb: 2,
            color: "#020A52",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(2, 10, 82, 0.04)",
            },
          }}
          onClick={handleForgotPassword}
        >
          {forgotPasswordLoading ? "Sending..." : "Forgot Password?"}
        </Button>
        {error && forgotPasswordError && (
          <Typography
            color="error"
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: 14,
            }}
          >
            Unable to reset password. Please verify your email address is
            correct. For additional help, please contact assistant@elm-ai.com.
          </Typography>
        )}
        {error && !forgotPasswordError && (
          <Typography
            color="error"
            sx={{
              mt: 2, // Add margin-top to ensure the error message is fully visible
              textAlign: "center", // Center align the error message
              fontSize: 14,
            }}
          >
            Unable to login. Please verify your credentials are correct. For
            additional help, please contact assistant@elm-ai.com.
          </Typography>
        )}
      </form>
    </>
  );

  const renderVerifyCode = () => (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 24,
          lineHeight: "32px",
          color: "#101828",
          textAlign: "center",
          mb: 2,
        }}
      >
        Enter Verification Code
      </Typography>

      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 16,
          lineHeight: "24px",
          color: "#667085",
          textAlign: "center",
        }}
      >
        We've sent a 6-digit code to {email}. Enter it below to continue.
      </Typography>

      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 16,
          lineHeight: "24px",
          color: "#667085",
          textAlign: "center",
        }}
      >
        The code will expire in 20 minutes.
      </Typography>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 16,
          lineHeight: "24px",
          color: "#020A52",
          textAlign: "center",
          mb: 4,
        }}
      >
        Please keep this page open when you retrieve the code from your email.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 3 }}>
        {code.map((digit, index) => (
          <TextField
            key={index}
            id={`code-${index}`}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleCodeKeyDown(index, e)}
            variant="outlined"
            inputProps={{
              style: {
                textAlign: "center",
                fontSize: "20px",
                fontWeight: "bold",
              },
            }}
            sx={{
              width: 50,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "8px",
                },
              },
              "& .MuiOutlinedInput-input": {
                height: "50px",
                padding: "0",
              },
            }}
          />
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleCodeSubmit}
        disabled={code.join("").length !== 6 || forgotPasswordLoading}
        sx={{
          mb: 2,
          backgroundColor: "#020A52",
          borderRadius: "8px",
          textTransform: "none",
          padding: "10px 16px",
          fontSize: 16,
          fontWeight: 600,
          lineHeight: "24px",
        }}
      >
        {forgotPasswordLoading ? "Verifying..." : "Verify Code"}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={handleBackToLogin}
        sx={{
          color: "#020A52",
          textTransform: "none",
          fontSize: 14,
        }}
      >
        Back to Login
      </Button>

      {forgotPasswordError && (
        <Typography
          color="error"
          sx={{
            mt: 2,
            textAlign: "center",
            fontSize: 14,
          }}
        >
          Invalid or expired verification code. Please check your most recent
          email for the latest code. If your code has expired, click "Back to
          Login" to restart the password reset process. Need help? Contact us at
          assistant@elm-ai.com.
        </Typography>
      )}
    </>
  );

  const renderResetPassword = () => (
    <>
      <Typography
        style={{
          fontWeight: 600,
          fontSize: 24,
          lineHeight: '32px',
          color: '#101828',
          textAlign: 'center',
          marginBottom: '8px',
        }}
      >
        Reset Password
      </Typography>
      <Typography
        style={{
          fontWeight: 400,
          fontSize: 16,
          lineHeight: '24px',
          color: '#667085',
          textAlign: 'center',
          marginBottom: '32px',
        }}
      >
        Enter your new password below (minimum 5 characters).
      </Typography>

      <form onSubmit={handlePasswordReset}>
        <TextField
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={!!passwordError && passwordError.includes('Password must be')}
          helperText={passwordError && passwordError.includes('Password must be') ? passwordError : ''}
          fullWidth
          variant="outlined"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderRadius: "8px",
              },
            },
            "& .MuiOutlinedInput-input": {
              height: "40px",
              padding: "0 14px",
            },
            "& .MuiInputLabel-outlined": {
              lineHeight: "40px",
              transform: "translate(14px, 0px) scale(1)",
            },
            "& .MuiInputLabel-shrink": {
              transform: "translate(14px, -14px) scale(0.75)",
            },
          }}
          InputProps={{
            style: {
              fontFamily: "Inter, sans-serif",
              alignItems: "center",
              fontSize: "14px",
            },
          }}
          InputLabelProps={{
            style: {
              fontSize: "12px",
              margin: 0,
              padding: 0,
            },
          }}
        />

        <TextField
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={!!passwordError && !passwordError.includes('Password must be')}
          helperText={passwordError && !passwordError.includes('Password must be') ? passwordError : ''}
          fullWidth
          variant="outlined"
          sx={{
            mb: 6,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderRadius: "8px",
              },
            },
            "& .MuiOutlinedInput-input": {
              height: "40px",
              padding: "0 14px",
            },
            "& .MuiInputLabel-outlined": {
              lineHeight: "40px",
              transform: "translate(14px, 0px) scale(1)",
            },
            "& .MuiInputLabel-shrink": {
              transform: "translate(14px, -14px) scale(0.75)",
            },
          }}
          InputProps={{
            style: {
              fontFamily: "Inter, sans-serif",
              alignItems: "center",
              fontSize: "14px",
            },
          }}
          InputLabelProps={{
            style: {
              fontSize: "12px",
              margin: 0,
              padding: 0,
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={forgotPasswordLoading || newPassword.length < 5}
          sx={{
            mb: 2,
            backgroundColor: '#020A52',
            borderRadius: '8px',
            textTransform: 'none',
            gap: '6px',
            padding: '10px 16px',
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '24px',
          }}
        >
          {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={handleBackToLogin}
          sx={{
            mb: 2,
            color: "#020A52",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(2, 10, 82, 0.04)",
            },
          }}
        >
          Back to Login
        </Button>

        {forgotPasswordError && (
          <Typography
            color="error"
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: 14,
            }}
          >
            {forgotPasswordError}
          </Typography>
        )}
      </form>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "99vh",
        fontFamily: "Inter",
        boxShadow:
          "0px 3px 3px -2px rgb(0 0 0 / 0%), 0px 3px 4px 0px rgb(0 0 0 / 5%), 0px 1px 8px 0px rgb(0 0 0 / 0%)",
        backgroundColor: "#F9FAFB",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          borderRadius: "12px",
          gap: 3,
          padding: "32px 40px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 400,
          minHeight: 600,
          boxShadow:
            "0px 6px 6px -3px rgb(0 0 0 / 0%), 0px 10px 14px 1px rgb(0 0 0 / 6%), 0px 4px 18px 3px rgb(0 0 0 / 1%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            py: 2,
            px: 1,
          }}
        >
          <img src="/Elm_Logo.svg" alt="Logo" style={{ width: 52, height: 49 }} />
        </Box>

        {mode === 'login' && renderLoginForm()}
        {mode === 'verify' && renderVerifyCode()}
        {mode === 'reset' && renderResetPassword()}
      </Paper>
    </Box>
  );
};

export default Login;
